import React from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import useLuxPlusRenewalBonus from 'luxPlus/hooks/useLuxPlusRenewalBonus'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import useJoinSociete from 'luxPlus/hooks/useJoinSociete'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { getHasPaidLuxPlusMembershipInCurrentRegion } from 'luxPlus/selectors/membership'
import { getIsWithinRenewalGracePeriod } from 'luxPlus/selectors/featureToggle'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getShouldHideLoyaltyStatusCreditsAndNights from 'loyalty/selectors/getShouldHideLoyaltyStatusCreditsAndNights'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import LuxPlusRenewalBonusTimeline, { renewalBonusMessages, useNoProgressionHeading, RenewalTextSkeleton, RenewalBonusGiftIcon } from './LuxPlusRenewalBonusTimeline'

const Wrapper = styled.div`
  padding-block: ${rem(40)};
`

const Card = styled.div`
  border: 1px solid ${(props) => props.theme.palette.product.luxPlus.foreground};
  border-radius: ${(props) => props.theme.borderRadius.L};
  background-color: ${(props) => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(20)};
  margin-bottom: ${rem(12)};
`

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: ${rem(40)};
  }
`

// Left column: the title, plus the CTA when the layout is wrapped (992–1100px).
const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};

  ${mediaQueryUp.desktop} {
    flex: 1;
    max-width: ${rem(371)};
  }

  // With the progression collapsed there's no timeline beside the heading, so let it fill the row.
  &.full-width {
    ${mediaQueryUp.desktop} {
      max-width: none;
    }
  }
`

// CTA under the title — only while wrapped (992–1100px).
const CtaUnderTitle = styled.div`
  display: none;

  @media (min-width: 992px) and (max-width: 1099.98px) {
    display: block;
  }
`

// CTA after the track — mobile (its own line) and wide desktop (3rd column),
// but not while wrapped (it moves under the title then).
const CtaAfterTrack = styled.div`
  display: block;

  @media (min-width: 992px) and (max-width: 1099.98px) {
    display: none;
  }
`

const messages = defineMessages({
  eyebrow: {
    id: 'CVefD_JGOaeXWM5U3ilO',
    defaultMessage: 'Annual renewal bonus',
    description: 'Eyebrow label above the LuxPlus annual renewal bonus banner heading',
  },
  heading: {
    id: 'AYTzL_ve8iWH3mShIfIz',
    defaultMessage: 'Your renewal bonus increases with each year of membership',
    description: 'Heading for the LuxPlus annual renewal bonus banner',
  },
  becomeMember: {
    id: 'mF8SjaPoR12xvc4K5Ihq',
    defaultMessage: 'Become a member',
    description: 'CTA on the renewal bonus banner shown to non-members to start a LuxPlus membership',
  },
  joinSocieteHint: {
    id: 'KUp6joks_QjjCg6Za8ZP',
    defaultMessage: 'Join <link>Société</link> for free before your renewal date to receive your renewal bonus.',
    description: 'Subline with a join-Société link on the renewal bonus banner for signed-in visitors who have not joined Société',
  },
})

function LuxPlusRenewalBonusBanner() {
  const intl = useIntl()
  const isEnabled = useAppSelector(getIsLoyaltyEnabled)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isPaidMember = useAppSelector(getHasPaidLuxPlusMembershipInCurrentRegion)
  const isSocieteMember = useAppSelector(getHasLoyaltyAccount)
  const isWithinRenewalGracePeriod = useAppSelector(getIsWithinRenewalGracePeriod)
  const hideStatusCredits = useAppSelector(getShouldHideLoyaltyStatusCreditsAndNights)
  const { steps, shouldHideRenewalProgression, isLoading } = useLuxPlusRenewalBonus()
  const { handleBecomeMember } = useLuxPlusLandingActions()
  const joinSociete = useJoinSociete()
  const formatNoProgressionHeading = useNoProgressionHeading()

  // Non-members get the "Become a member" CTA; their timeline shows every tier as upcoming.
  const showBecomeMember = !isPaidMember

  // Signed-in visitors who have not joined Société see this prompt, including LuxPlus+ members.
  // Logged-out visitors have no renewal date, so this prompt stays hidden.
  const showJoinSocieteHint = isLoggedIn && !isSocieteMember && !isLoading

  // When the ladder is collapsed, the heading states the member's next renewal reward.
  // Its amounts come from the current step, falling back to the 1st renewal when there is no
  // current step — e.g. an Infinite member who isn't a LuxPlus+ member yet (no subscription,
  // so no progression) still sees the 1st reward they'd get on joining. Infinite members don't
  // accrue status credits, so those are hidden.
  const nextReward = steps.find((step) => step.state === 'current') ?? steps[0]

  if (!isEnabled) return null

  const becomeMemberButton = (
    <ProductPaletteProvider palette="lux-plus">
      <TextButton kind="primary" fit="mobile-full-width" onClick={handleBecomeMember}>
        {intl.formatMessage(messages.becomeMember)}
      </TextButton>
    </ProductPaletteProvider>
  )

  return (
    <Wrapper>
      <LayoutContainer>
        <Card>
          <Layout>
            <LeftColumn className={clsx({ 'full-width': shouldHideRenewalProgression && !isLoading })}>
              <Group direction="vertical" gap={12}>
                <Group direction="horizontal" gap={8} verticalAlign="center">
                  <RenewalBonusGiftIcon size="XS" />
                  <BodyTextBlock variant="medium" weight="bold" colour="lux-plus">
                    {intl.formatMessage(messages.eyebrow)}
                  </BodyTextBlock>
                </Group>
                {isLoading ? (
                  <RenewalTextSkeleton lineWidths={[280, 200]} height={20} />
                ) : (
                  <HeadingTextBlock variant="heading3" colour="lux-plus">
                    {shouldHideRenewalProgression
                      ? formatNoProgressionHeading(nextReward, { hideStatusCredits })
                      : intl.formatMessage(messages.heading)}
                  </HeadingTextBlock>
                )}
                {showJoinSocieteHint && (
                  <BodyTextBlock variant="medium" colour="normal-contrast">
                    <FormattedMessage
                      {...messages.joinSocieteHint}
                      values={{ link: (chunks) => <TextLink weight="regular" onClick={joinSociete}>{chunks}</TextLink> }}
                    />
                  </BodyTextBlock>
                )}
              </Group>
              {showBecomeMember && <CtaUnderTitle>{becomeMemberButton}</CtaUnderTitle>}
            </LeftColumn>

            {(isLoading || !shouldHideRenewalProgression) && (
              <LuxPlusRenewalBonusTimeline
                steps={steps}
                isLoading={isLoading}
                desktopMaxWidthRem={560}
                currentStepCaption={intl.formatMessage(isWithinRenewalGracePeriod ? renewalBonusMessages.currentStepUnlock : renewalBonusMessages.currentStepAlmostHere)}
              />
            )}

            {showBecomeMember && <CtaAfterTrack>{becomeMemberButton}</CtaAfterTrack>}
          </Layout>
        </Card>
        <CaptionTextBlock variant="medium" colour="light-foreground-alt">
          {intl.formatMessage(renewalBonusMessages.footnote, { membershipName: LUXURY_PLUS.PROGRAM_NAME })}
        </CaptionTextBlock>
      </LayoutContainer>
    </Wrapper>
  )
}

export default LuxPlusRenewalBonusBanner
